// src/components/locations/forms/RegionForm.tsx
import React from "react";

import type {
  LocationCountry,
  LocationRegion,
} from "@/api/features/locations/types";

import { FormSection } from "@/pages/locations/components/form/FormSection";
import { FormField } from "@/pages/locations/components/form/FormField";
import { controlClass } from "@/pages/locations/components/form/controlClass";
import {
  FormActions,
  FormError,
} from "@/pages/locations/components/form/FormActions";
import { CountryPicker } from "@/pages/locations/components/CountryPicker";
import { RecordMeta } from "@/pages/locations/components/form/RecordMeta";
import { useLocationForm } from "@/pages/locations/components/form/useLocationForm";
import {
  apiErrorSummary,
  collectErrors,
  maxLengthRule,
  requiredText,
} from "@/pages/locations/formErrors";

export type RegionFormValues = {
  countryIso2: string;
  name: string;
  code: string;
};

type Props = {
  mode: "create" | "edit";
  value?: LocationRegion;
  countries: LocationCountry[];
  countriesLoading?: boolean;
  /** Country pre-selected from the list the user came from. */
  defaultCountryIso2?: string;
  isSubmitting: boolean;
  error?: unknown;
  onSubmit: (values: RegionFormValues, options: { addAnother: boolean }) => void;
  onCancel: (isDirty: boolean) => void;
  resetSignal?: number;
};

const RegionForm: React.FC<Props> = ({
  mode,
  value,
  countries,
  countriesLoading = false,
  defaultCountryIso2 = "",
  isSubmitting,
  error,
  onSubmit,
  onCancel,
  resetSignal = 0,
}) => {
  const initial = React.useMemo<RegionFormValues>(
    () => ({
      countryIso2: String(
        value?.countryIso2 ?? defaultCountryIso2 ?? ""
      ).toUpperCase(),
      name: value?.name ?? "",
      code: value?.code ?? "",
    }),
    [value, defaultCountryIso2]
  );

  const validate = React.useCallback(
    (candidate: RegionFormValues) =>
      collectErrors({
        countryIso2: candidate.countryIso2
          ? undefined
          : "Pick the country this region belongs to.",
        name: requiredText(candidate.name, "Region name"),
        code: maxLengthRule(candidate.code, 10, "Code"),
      }),
    []
  );

  const { values, setField, errorFor, isDirty, formRef, handleSubmit, resetTo } =
    useLocationForm<RegionFormValues>({
      initial,
      validate,
      idPrefix: "region",
      error,
      onValid: (v, options) =>
        onSubmit(
          {
            countryIso2: v.countryIso2.trim().toUpperCase(),
            name: v.name.trim(),
            code: v.code.trim(),
          },
          options
        ),
    });

  React.useEffect(() => {
    if (resetSignal > 0) {
      // Keep the country: the next region is almost always in the same one.
      resetTo({ countryIso2: values.countryIso2, name: "", code: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetSignal]);

  const movingCountry =
    mode === "edit" &&
    value?.countryIso2 &&
    values.countryIso2 !== String(value.countryIso2).toUpperCase();

  return (
    <form
      ref={formRef}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(false);
      }}
    >
      <FormError message={apiErrorSummary(error)} />

      <FormSection
        title="Where it belongs"
        description="A region is a state or province inside one country."
      >
        <FormField
          id="region-countryIso2"
          label="Country"
          required
          error={errorFor("countryIso2")}
          hint={
            movingCountry
              ? "Saving will move this region — and its cities stay attached to it — into the new country."
              : "Start typing to filter the list."
          }
        >
          {(a) => (
            <CountryPicker
              id={a.id}
              describedBy={a["aria-describedby"]}
              value={values.countryIso2}
              onChange={(iso2) => setField("countryIso2", iso2)}
              countries={countries}
              disabled={isSubmitting || countriesLoading}
              error={errorFor("countryIso2")}
            />
          )}
        </FormField>
      </FormSection>

      <FormSection title="Region details">
        <FormField
          id="region-name"
          label="Region name"
          required
          error={errorFor("name")}
          hint="The state or province name, e.g. Maharashtra."
        >
          {(a) => (
            <input
              {...a}
              value={values.name}
              onChange={(e) => setField("name", e.target.value)}
              disabled={isSubmitting}
              autoFocus
              placeholder="Maharashtra"
              className={controlClass(errorFor("name"))}
            />
          )}
        </FormField>

        <FormField
          id="region-code"
          label="Code"
          error={errorFor("code")}
          hint="Optional short code, e.g. MH. Up to 10 characters."
        >
          {(a) => (
            <input
              {...a}
              value={values.code}
              onChange={(e) => setField("code", e.target.value.toUpperCase())}
              disabled={isSubmitting}
              maxLength={10}
              placeholder="MH"
              className={controlClass(errorFor("code"), "w-40 font-mono uppercase")}
            />
          )}
        </FormField>
      </FormSection>

      {mode === "edit" && value && <RecordMeta record={value} />}

      <FormActions
        submitLabel={mode === "create" ? "Create region" : "Save changes"}
        submittingLabel="Saving…"
        isSubmitting={isSubmitting}
        onCancel={() => onCancel(isDirty)}
        onSaveAndAddAnother={mode === "create" ? () => handleSubmit(true) : undefined}
      />
    </form>
  );
};

export default RegionForm;
